"use client";

import { Activity } from "@entities/activity";
import { Close } from "@shared/assets/icons";
import { RichText } from "@shared/ui/rich-text";
import Image from "next/image";
import { forwardRef, useEffect, useRef } from "react";

export type CardProps = {
  activity: Activity;
  scrollTo?: boolean;
  onClose?: () => void;
  className?: string;
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { activity: { name, image, content }, scrollTo, onClose, className },
  forwardedRef
) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollTo)
      setTimeout(() =>
        ref.current?.scrollIntoView({ block: "start", behavior: "smooth" })
      );
  }, [scrollTo]);

  return (
    <div
      ref={(element) => {
        ref.current = element;
        if (!forwardedRef) return;

        if (typeof forwardedRef === "function") forwardedRef(element);
        else forwardedRef.current = element;
      }}
      className={[
        "relative flex flex-col justify-center items-center w-full h-full gap-4 p-8 bg-gray-200 rounded pointer-events-auto scroll-mt-48",
        className,
      ].join(" ")}
    >
      <Image
        src={image}
        alt="Image"
        width={56}
        height={56}
        className="w-14 h-14"
      />

      <RichText
        className={"text-lg text-persian-plum-900 text-center lg:[&>br]:hidden"}
        message={name}
      />

      <RichText
        className={
          "text-base text-black-pearl-950 mt-8 px-8 lg:px-12 2xl:px-28 [&>li]:ml-4"
        }
        message={content}
      />

      <Close
        className={"absolute right-10 top-10 cursor-pointer"}
        onClick={onClose}
      />
    </div>
  );
});
